import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { authedHeaders, API, useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Ban, ShieldCheck, ShieldOff, UserCheck, Eye } from "lucide-react";
import {
  LivePanel, ReportsPanel, AuditPanel, AnnouncementsPanel, UserProfileDialog,
} from "@/pages/admin/AdminPanels";

function Stat({ label, value }) {
  return (
    <Card className="p-4">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-mono font-semibold text-2xl mt-1">{value ?? "—"}</p>
    </Card>
  );
}

export function Admin() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [games, setGames] = useState([]);
  const [query, setQuery] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { headers: authedHeaders() });
      setStats(data);
    } catch (e) {
      // Forbidden / unauthorised handled in effect below
    }
  }, []);

  const fetchUsers = useCallback(async (q = "") => {
    setUsersLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, {
        headers: authedHeaders(),
        params: q ? { q, limit: 100 } : { limit: 100 },
      });
      setUsers(data.users);
    } catch (e) {
      // ignore
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchGames = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/games`, {
        headers: authedHeaders(),
        params: { limit: 50 },
      });
      setGames(data.games);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (user.role !== "admin" && user.role !== "super_admin") {
      navigate("/");
      return;
    }
    fetchStats();
    fetchUsers();
    fetchGames();
  }, [user, loading, navigate, fetchStats, fetchUsers, fetchGames]);

  const onSuspend = async (u, suspend) => {
    const path = suspend ? "suspend" : "unsuspend";
    try {
      await axios.post(`${API}/admin/users/${u.id}/${path}`, {}, { headers: authedHeaders() });
      toast.success(`${u.username} ${suspend ? "suspended" : "reinstated"}.`);
      fetchUsers(query);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed.");
    }
  };
  const onSetRole = async (u, role) => {
    try {
      await axios.post(`${API}/admin/users/${u.id}/role`, { role }, { headers: authedHeaders() });
      toast.success(`${u.username} is now ${role}.`);
      fetchUsers(query);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed.");
    }
  };

  const byRuleset = useMemo(() => stats?.by_ruleset || [], [stats]);

  if (loading || !user || (user.role !== "admin" && user.role !== "super_admin")) {
    return (
      <Layout>
        <section className="max-w-6xl mx-auto px-4 py-16 text-center text-muted-foreground">
          {loading ? "Loading…" : "Forbidden."}
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Operations</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-1">Admin dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Signed in as <span className="font-medium text-foreground">{user.username}</span> · role: {user.role === "super_admin" ? "super-admin" : "admin"}
            </p>
          </div>
          <Badge variant="secondary" className="rounded-full">
            <ShieldCheck className="h-3.5 w-3.5 mr-1" /> {user.role === "super_admin" ? "Super admin" : "Admin"}
          </Badge>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Total users" value={stats?.totals.users} />
          <Stat label="New today" value={stats?.totals.users_new_today} />
          <Stat label="Active today" value={stats?.totals.users_active_today} />
          <Stat label="Suspended" value={stats?.totals.users_suspended} />
          <Stat label="Admins" value={stats?.totals.users_admin} />
          <Stat label="Games total" value={stats?.totals.games_total} />
          <Stat label="Games today" value={stats?.totals.games_today} />
          <Card className="p-4">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">By ruleset</p>
            <div className="mt-2 space-y-1 text-xs font-mono">
              {byRuleset.slice(0, 3).map((r) => (
                <div key={r.ruleset_id} className="flex justify-between">
                  <span className="truncate mr-2">{r.ruleset_id}</span>
                  <span>{r.count}</span>
                </div>
              ))}
              {byRuleset.length === 0 && <span className="text-muted-foreground">No data yet</span>}
            </div>
          </Card>
        </div>

        <Tabs defaultValue="live" className="mt-10">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="live" data-testid="admin-tab-live">Live</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="admin-tab-announcements">Announcements</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit</TabsTrigger>
            <TabsTrigger value="games" data-testid="admin-tab-games">Games</TabsTrigger>
            <TabsTrigger value="variants" data-testid="admin-tab-variants">Variants</TabsTrigger>
          </TabsList>

          <TabsContent value="live" className="mt-4">
            <LivePanel />
          </TabsContent>

          <TabsContent value="reports" className="mt-4">
            <ReportsPanel />
          </TabsContent>

          <TabsContent value="announcements" className="mt-4">
            <AnnouncementsPanel />
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <AuditPanel />
          </TabsContent>

          <TabsContent value="users" className="mt-4">
            <div className="flex gap-2 mb-3 flex-wrap">
              <Input
                data-testid="admin-search-users"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by username, email, display name…"
                className="max-w-sm"
              />
              <Button onClick={() => fetchUsers(query)} className="rounded-full">
                Search
              </Button>
              {query && (
                <Button variant="ghost" className="rounded-full" onClick={() => { setQuery(""); fetchUsers(""); }}>
                  Clear
                </Button>
              )}
            </div>
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Username</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Games</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersLoading && (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>
                  )}
                  {!usersLoading && users.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">No users found.</TableCell></TableRow>
                  )}
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`admin-user-row-${u.username}`}>
                      <TableCell className="font-medium">{u.username}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell className="text-muted-foreground">{u.country || "—"}</TableCell>
                      <TableCell>
                        <Badge variant={u.role === "super_admin" ? "default" : u.role === "admin" ? "default" : "outline"} className="rounded-full">
                          {u.role === "super_admin" ? "super-admin" : u.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {u.suspended ? (
                          <Badge variant="destructive" className="rounded-full">Suspended</Badge>
                        ) : (
                          <Badge variant="secondary" className="rounded-full">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono">{u.stats?.played || 0}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5">
                          <UserProfileDialog userId={u.id}>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="rounded-full"
                              data-testid={`admin-view-${u.username}`}
                              title="View profile"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </UserProfileDialog>
                          {user.role === "super_admin" && u.role !== "super_admin" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="rounded-full"
                              data-testid={`admin-superadmin-${u.username}`}
                              onClick={() => onSetRole(u, "super_admin")}
                              disabled={u.id === user.id}
                              title="Promote to super-admin"
                            >
                              ★
                            </Button>
                          )}
                          {(u.role === "admin" || u.role === "super_admin") ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="rounded-full"
                              data-testid={`admin-demote-${u.username}`}
                              onClick={() => onSetRole(u, "user")}
                              disabled={u.id === user.id || (u.role === "super_admin" && user.role !== "super_admin")}
                              title="Demote to user"
                            >
                              <ShieldOff className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="rounded-full"
                              data-testid={`admin-promote-${u.username}`}
                              onClick={() => onSetRole(u, "admin")}
                              title="Promote to admin"
                            >
                              <UserCheck className="h-4 w-4" />
                            </Button>
                          )}
                          {u.suspended ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full"
                              data-testid={`admin-unsuspend-${u.username}`}
                              onClick={() => onSuspend(u, false)}
                            >
                              Reinstate
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="destructive"
                              className="rounded-full"
                              data-testid={`admin-suspend-${u.username}`}
                              onClick={() => onSuspend(u, true)}
                              disabled={u.id === user.id || (u.role === "super_admin" && user.role !== "super_admin")}
                            >
                              <Ban className="h-4 w-4 mr-1" /> Suspend
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="games" className="mt-4">
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Player</TableHead>
                    <TableHead>Ruleset</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Moves</TableHead>
                    <TableHead>Captures</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {games.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No games recorded yet.</TableCell></TableRow>
                  )}
                  {games.map((g) => (
                    <TableRow key={g.id} data-testid={`admin-game-row-${g.id}`}>
                      <TableCell className="font-medium">{g.username}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{g.ruleset_id}</TableCell>
                      <TableCell className="text-xs">{g.mode}{g.difficulty ? ` · ${g.difficulty}` : ""}</TableCell>
                      <TableCell>
                        <Badge
                          variant={g.outcome === "win" ? "default" : g.outcome === "loss" ? "destructive" : "outline"}
                          className="rounded-full"
                        >
                          {g.outcome}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono">{g.moves}</TableCell>
                      <TableCell className="font-mono">{g.captures}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(g.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="variants" className="mt-4">
            <Card className="p-5">
              <p className="font-display text-lg">Variants overview</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Status badges (Playable / Beta / In Testing / Coming Soon) currently live in the source code
                (<code className="text-xs">/app/frontend/src/game/rulesets.js</code>). They will be moved to a
                database-backed table in Milestone 3 so admins can toggle a ruleset live without a deploy.
              </p>
              <p className="text-xs text-muted-foreground mt-3">
                Activity by ruleset:
              </p>
              <ul className="mt-2 text-sm font-mono space-y-1">
                {byRuleset.length === 0 && <li className="text-muted-foreground">No games recorded yet.</li>}
                {byRuleset.map((r) => (
                  <li key={r.ruleset_id} className="flex justify-between">
                    <span>{r.ruleset_id}</span>
                    <span>{r.count} games</span>
                  </li>
                ))}
              </ul>
            </Card>
          </TabsContent>
        </Tabs>
      </section>
    </Layout>
  );
}
